import { useState, useEffect } from 'react';
import { ImagePreview, MediaPickerModal } from '../../components/carousel';
import backgroundsConfig, { getRecommendedSize, getButtonStyleDescription } from '../../config/backgrounds.config';
import './BackgroundEditModal.css';

/**
 * BackgroundEditModal Component
 * Modal dialog for editing page background
 *
 * Props:
 * - background: PageBackground object to edit
 * - isOpen: boolean - Whether modal is open
 * - onClose: () => void - Close handler
 * - onSave: (id, data) => Promise - Save handler
 * - isSaving: boolean - Saving state
 */
function BackgroundEditModal({ background, isOpen, onClose, onSave, isSaving }) {
  const [formData, setFormData] = useState({
    imageUrl: '',
    description: '',
    title: '',
    subtitle: '',
    buttonText: '',
    buttonLink: '',
    buttonStyle: '',
    readMoreContent: '',
  });
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  const isHomePage = background?.page === 'home';

  // Initialize form data when background changes
  useEffect(() => {
    if (background) {
      setFormData({
        imageUrl: background.imageUrl || '',
        description: background.description || '',
        title: background.title || '',
        subtitle: background.subtitle || '',
        buttonText: background.buttonText || '',
        buttonLink: background.buttonLink || '',
        buttonStyle: background.buttonStyle || '',
        readMoreContent: background.readMoreContent || '',
      });
      setErrors({});
      setIsDirty(false);
    }
  }, [background]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSaving, onClose]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Handle image selection from media picker
  const handleImageSelect = (url) => {
    setFormData((prev) => ({ ...prev, imageUrl: url }));
    setIsDirty(true);
    if (errors.imageUrl) {
      setErrors((prev) => ({ ...prev, imageUrl: null }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};
    const { validation } = backgroundsConfig;

    if (!formData.imageUrl.trim()) {
      newErrors.imageUrl = 'Image URL is required';
    } else {
      try {
        new URL(formData.imageUrl);
      } catch {
        newErrors.imageUrl = 'Please enter a valid URL';
      }
    }

    if (formData.description && formData.description.length > validation.description.maxLength) {
      newErrors.description = `Description must be at most ${validation.description.maxLength} characters`;
    }

    if (isHomePage) {
      if (formData.title && formData.title.length > validation.title.maxLength) {
        newErrors.title = `Title must be at most ${validation.title.maxLength} characters`;
      }
      if (formData.buttonText && formData.buttonText.length > validation.buttonText.maxLength) {
        newErrors.buttonText = `Button text must be at most ${validation.buttonText.maxLength} characters`;
      }
      if (formData.buttonLink && formData.buttonLink.length > validation.buttonLink.maxLength) {
        newErrors.buttonLink = `Button link must be at most ${validation.buttonLink.maxLength} characters`;
      }
      if (formData.readMoreContent && formData.readMoreContent.length > validation.readMoreContent.maxLength) {
        newErrors.readMoreContent = `Read more content must be at most ${validation.readMoreContent.maxLength} characters`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const data = {
      imageUrl: formData.imageUrl.trim(),
      description: formData.description.trim() || null,
    };

    // Include banner fields only for home page
    if (isHomePage) {
      data.title = formData.title.trim() || null;
      data.subtitle = formData.subtitle.trim() || null;
      data.buttonText = formData.buttonText.trim() || null;
      data.buttonLink = formData.buttonLink.trim() || null;
      data.buttonStyle = formData.buttonStyle || null;
      data.readMoreContent = formData.readMoreContent.trim() || null;
    }

    try {
      await onSave(background.id, data);
    } catch (err) {
      // Error handled by parent
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  // Get page label
  const getPageLabel = (page) => {
    return page === 'home' ? 'Home Page' : 'Product Listing';
  };

  const recommendedSize = getRecommendedSize(background?.page);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className={`modal background-edit-modal ${isHomePage ? 'background-edit-modal--home' : ''}`} role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>Edit {getPageLabel(background?.page)} Background</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className={`form-grid ${isHomePage ? 'form-grid--home' : ''}`}>
              {/* Left Column - Form Fields (for home page) or Image Preview */}
              {isHomePage ? (
                <div className="form-column">
                  {/* Title */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="title">
                      Banner Title
                    </label>
                    <input
                      id="title"
                      name="title"
                      type="text"
                      className={`form-input ${errors.title ? 'is-invalid' : ''}`}
                      placeholder="e.g., 2026 WORLD CUP COLLECTION"
                      value={formData.title}
                      onChange={handleChange}
                      disabled={isSaving}
                    />
                    {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                  </div>

                  {/* Subtitle */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="subtitle">
                      Subtitle
                    </label>
                    <textarea
                      id="subtitle"
                      name="subtitle"
                      className={`form-input ${errors.subtitle ? 'is-invalid' : ''}`}
                      placeholder="e.g., Glory captured in a frame"
                      rows={2}
                      value={formData.subtitle}
                      onChange={handleChange}
                      disabled={isSaving}
                    />
                    {errors.subtitle && <div className="invalid-feedback">{errors.subtitle}</div>}
                  </div>

                  {/* Button Text */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="buttonText">
                      Button Text
                    </label>
                    <input
                      id="buttonText"
                      name="buttonText"
                      type="text"
                      className={`form-input ${errors.buttonText ? 'is-invalid' : ''}`}
                      placeholder="e.g., COMING SOON"
                      value={formData.buttonText}
                      onChange={handleChange}
                      disabled={isSaving}
                    />
                    {errors.buttonText && <div className="invalid-feedback">{errors.buttonText}</div>}
                  </div>

                  {/* Button Link */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="buttonLink">
                      Button Link
                    </label>
                    <input
                      id="buttonLink"
                      name="buttonLink"
                      type="text"
                      className={`form-input ${errors.buttonLink ? 'is-invalid' : ''}`}
                      placeholder="e.g., /product-listing"
                      value={formData.buttonLink}
                      onChange={handleChange}
                      disabled={isSaving}
                    />
                    {errors.buttonLink && <div className="invalid-feedback">{errors.buttonLink}</div>}
                    <p className="form-help">Leave empty if using Read More Content below</p>
                  </div>

                  {/* Button Style */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="buttonStyle">
                      Button Style
                    </label>
                    <select
                      id="buttonStyle"
                      name="buttonStyle"
                      className={`form-select ${errors.buttonStyle ? 'is-invalid' : ''}`}
                      value={formData.buttonStyle}
                      onChange={handleChange}
                      disabled={isSaving}
                    >
                      <option value="">Select a style...</option>
                      {backgroundsConfig.buttonStyles.map((style) => (
                        <option key={style.value} value={style.value}>
                          {style.label}
                        </option>
                      ))}
                    </select>
                    {errors.buttonStyle && <div className="invalid-feedback">{errors.buttonStyle}</div>}

                    {/* Button Style Preview */}
                    {formData.buttonStyle && (
                      <div className="button-style-preview">
                        <span className="preview-label">Preview:</span>
                        <div className="preview-container">
                          <span className={`preview-button preview-button--${formData.buttonStyle}`}>
                            {formData.buttonText || 'BUTTON TEXT'}
                          </span>
                        </div>
                        <span className="preview-description">
                          {getButtonStyleDescription(formData.buttonStyle)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Read More Content */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="readMoreContent">
                      Read More Content
                    </label>
                    <textarea
                      id="readMoreContent"
                      name="readMoreContent"
                      className={`form-input ${errors.readMoreContent ? 'is-invalid' : ''}`}
                      placeholder="A short text snippet shown when button is clicked..."
                      rows={3}
                      value={formData.readMoreContent}
                      onChange={handleChange}
                      disabled={isSaving}
                      maxLength={backgroundsConfig.validation.readMoreContent.maxLength}
                    />
                    <div className="form-help-row">
                      <span className="form-help">
                        When set, clicking the button shows this content in a modal.
                      </span>
                      <span className={`char-count ${formData.readMoreContent.length > backgroundsConfig.validation.readMoreContent.maxLength * 0.9 ? 'char-count--warning' : ''}`}>
                        {formData.readMoreContent.length}/{backgroundsConfig.validation.readMoreContent.maxLength}
                      </span>
                    </div>
                    {errors.readMoreContent && <div className="invalid-feedback">{errors.readMoreContent}</div>}
                  </div>
                </div>
              ) : (
                /* Product Listing - Image Preview on Left */
                <div className="preview-column">
                  <label className="form-label">Background Image <span className="required">*</span></label>
                  <div className="image-preview-wrapper">
                    <ImagePreview
                      url={formData.imageUrl}
                      alt="Background preview"
                      aspectRatio="16:9"
                    />
                    <button
                      type="button"
                      className="change-image-btn"
                      onClick={() => setIsMediaPickerOpen(true)}
                      disabled={isSaving}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                        />
                      </svg>
                      Change Image
                    </button>
                    {errors.imageUrl && (
                      <div className="image-error">{errors.imageUrl}</div>
                    )}
                  </div>
                  <p className="preview-hint">
                    Click "Change Image" to select from Media Library
                  </p>
                  <div className="size-recommendation">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                      />
                    </svg>
                    <span>
                      Recommended size: <strong>{recommendedSize.width} x {recommendedSize.height}</strong> pixels
                    </span>
                  </div>
                </div>
              )}

              {/* Right Column - Description (for product listing) or Image Preview (for home) */}
              {isHomePage ? (
                <div className="preview-column">
                  <label className="form-label">Background Image <span className="required">*</span></label>
                  <div className="image-preview-wrapper">
                    <ImagePreview
                      url={formData.imageUrl}
                      alt="Background preview"
                      aspectRatio="16:9"
                    />
                    <button
                      type="button"
                      className="change-image-btn"
                      onClick={() => setIsMediaPickerOpen(true)}
                      disabled={isSaving}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                        />
                      </svg>
                      Change Image
                    </button>
                    {errors.imageUrl && (
                      <div className="image-error">{errors.imageUrl}</div>
                    )}
                  </div>
                  <p className="preview-hint">
                    Click "Change Image" to select from Media Library
                  </p>
                  <div className="size-recommendation">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                      />
                    </svg>
                    <span>
                      Recommended size: <strong>{recommendedSize.width} x {recommendedSize.height}</strong> pixels
                    </span>
                  </div>

                  {/* Description for home page */}
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label className="form-label" htmlFor="description">
                      Description (Optional)
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      className={`form-input ${errors.description ? 'is-invalid' : ''}`}
                      placeholder="Admin notes about this background..."
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                      disabled={isSaving}
                    />
                    {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                    <p className="form-help">
                      Internal reference only - not displayed on website.
                    </p>
                  </div>

                  <div className="info-box">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                      />
                    </svg>
                    <p>
                      Changes will sync automatically to the <strong>{getPageLabel(background?.page).toLowerCase()}</strong> on the live website.
                    </p>
                  </div>
                </div>
              ) : (
                /* Product Listing - Description on Right */
                <div className="form-column">
                  <div className="form-group">
                    <label className="form-label" htmlFor="description">
                      Description (Optional)
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      className={`form-input ${errors.description ? 'is-invalid' : ''}`}
                      placeholder="Admin notes about this background image..."
                      rows={6}
                      value={formData.description}
                      onChange={handleChange}
                      disabled={isSaving}
                    />
                    {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                    <p className="form-help">
                      This description is for internal reference only and won't be displayed on the website.
                    </p>
                  </div>

                  <div className="info-box">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                      />
                    </svg>
                    <p>
                      Changes will sync automatically to the <strong>{getPageLabel(background?.page).toLowerCase()}</strong> on the live website.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving || !isDirty}
            >
              {isSaving ? (
                <>
                  <span className="btn-spinner" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Media Picker Modal */}
      <MediaPickerModal
        isOpen={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        onSelect={handleImageSelect}
        currentUrl={formData.imageUrl}
      />
    </div>
  );
}

export default BackgroundEditModal;
